import React, { useState } from 'react';
import { FiCheckCircle, FiAlertCircle, FiEdit3 } from 'react-icons/fi';
import styled from 'styled-components';
import { theme } from '@theme';
import {
  FormSection,
  SectionTitle,
  FormGroup,
  CheckboxLabel,
  InfoBox,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount
} from './FormStyles';
import { Declaration } from '@/types/application-forms';

const SignatureCanvas = styled.canvas`
  width: 100%;
  height: 200px;
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  background: white;
  cursor: crosshair;
  touch-action: none;
`;

const SignatureContainer = styled.div`
  position: relative;
`;

const ClearButton = styled.button`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.error};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error}dd;
    transform: translateY(-2px);
  }

  svg {
    font-size: 14px;
  }
`;

const DeclarationText = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};

  h4 {
    font-size: ${theme.typography.sizes.lg};
    font-weight: ${theme.typography.weights.semibold};
    color: ${theme.colors.text};
    margin: 0 0 ${theme.spacing.md} 0;
  }

  p {
    font-size: ${theme.typography.sizes.base};
    color: ${theme.colors.text}90;
    line-height: 1.6;
    margin: 0;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.xl} 0;
`;

interface DeclarationSectionProps {
  data: Partial<Declaration>;
  onChange: (data: Partial<Declaration>) => void;
  readonly?: boolean;
  summary?: {
    total_revenus?: number;
    total_charges?: number;
    capacite_paiement?: number;
    completion_percentage?: number;
  };
}

export const DeclarationSection: React.FC<DeclarationSectionProps> = ({
  data,
  onChange,
  readonly = false,
  summary
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (readonly) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || readonly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;

    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = theme.colors.primary;
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Save signature as base64
    const signature = canvas.toDataURL();
    handleChange('signature', signature);
    handleChange('date_signature', new Date().toISOString());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    handleChange('signature', '');
    handleChange('date_signature', '');
  };

  const allChecked =
    data.informations_exactes &&
    data.fournir_documents &&
    data.autorisation_verification &&
    data.conditions_acceptees;

  return (
    <FormSection>
      <SectionTitle>
        <FiCheckCircle />
        Déclarations et Validation
      </SectionTitle>

      {summary && (
        <>
          <InfoBox $type="success">
            <FiCheckCircle />
            <p>
              Félicitations ! Votre dossier est complété à {Math.round(summary.completion_percentage || 0)}%.
              Veuillez vérifier les informations ci-dessous avant de signer.
            </p>
          </InfoBox>

          <CalculatedValue>
            <CalculatedLabel>Résumé de votre candidature</CalculatedLabel>
            {summary.total_revenus !== undefined && (
              <div style={{ marginTop: theme.spacing.sm }}>
                <p style={{ margin: 0, fontSize: theme.typography.sizes.sm, color: theme.colors.text + '80' }}>
                  Revenus mensuels totaux : <strong>{summary.total_revenus.toLocaleString()} FCFA</strong>
                </p>
              </div>
            )}
            {summary.total_charges !== undefined && (
              <div style={{ marginTop: theme.spacing.xs }}>
                <p style={{ margin: 0, fontSize: theme.typography.sizes.sm, color: theme.colors.text + '80' }}>
                  Charges mensuelles totales : <strong>{summary.total_charges.toLocaleString()} FCFA</strong>
                </p>
              </div>
            )}
            {summary.capacite_paiement !== undefined && (
              <div style={{ marginTop: theme.spacing.md }}>
                <CalculatedAmount>
                  Capacité de paiement estimée : {summary.capacite_paiement.toLocaleString()} FCFA/mois
                </CalculatedAmount>
              </div>
            )}
          </CalculatedValue>
        </>
      )}

      <DeclarationText>
        <h4>Déclaration sur l'honneur</h4>
        <p>
          Je soussigné(e), candidat(e) à l'attribution d'un logement, déclare sur l'honneur que
          les informations fournies dans ce formulaire sont exactes et complètes. Je m'engage à
          fournir tout document complémentaire qui pourrait m'être demandé et autorise la
          vérification de ces informations auprès des organismes concernés.
        </p>
      </DeclarationText>

      <CheckboxContainer>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={data.informations_exactes || false}
            onChange={e => handleChange('informations_exactes', e.target.checked)}
            disabled={readonly}
          />
          <strong>Je certifie sur l'honneur que les informations fournies sont exactes et complètes</strong>
        </CheckboxLabel>

        <CheckboxLabel>
          <input
            type="checkbox"
            checked={data.fournir_documents || false}
            onChange={e => handleChange('fournir_documents', e.target.checked)}
            disabled={readonly}
          />
          <strong>Je m'engage à fournir tout document complémentaire requis</strong>
        </CheckboxLabel>

        <CheckboxLabel>
          <input
            type="checkbox"
            checked={data.autorisation_verification || false}
            onChange={e => handleChange('autorisation_verification', e.target.checked)}
            disabled={readonly}
          />
          <strong>J'autorise la vérification des informations auprès des organismes concernés</strong>
        </CheckboxLabel>

        <CheckboxLabel>
          <input
            type="checkbox"
            checked={data.conditions_acceptees || false}
            onChange={e => handleChange('conditions_acceptees', e.target.checked)}
            disabled={readonly}
          />
          <strong>J'ai pris connaissance des conditions d'attribution et m'engage à les respecter</strong>
        </CheckboxLabel>
      </CheckboxContainer>

      {!allChecked && (
        <InfoBox $type="warning">
          <FiAlertCircle />
          <p>
            Vous devez cocher toutes les cases ci-dessus pour pouvoir soumettre votre candidature.
          </p>
        </InfoBox>
      )}

      <FormGroup>
        <label style={{
          display: 'block',
          fontSize: theme.typography.sizes.base,
          fontWeight: theme.typography.weights.semibold,
          color: theme.colors.text,
          marginBottom: theme.spacing.md
        }}>
          Signature électronique
        </label>

        <SignatureContainer>
          <SignatureCanvas
            ref={canvasRef}
            width={800}
            height={200}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {data.signature && !readonly && (
            <ClearButton onClick={clearSignature}>
              <FiEdit3 />
              Effacer
            </ClearButton>
          )}
        </SignatureContainer>

        <p style={{
          margin: `${theme.spacing.sm} 0 0 0`,
          fontSize: theme.typography.sizes.sm,
          color: theme.colors.text + '70',
          fontStyle: 'italic'
        }}>
          {readonly
            ? 'Signature enregistrée'
            : 'Signez dans le cadre ci-dessus avec votre souris ou votre doigt'
          }
        </p>
      </FormGroup>

      {data.date_signature && (
        <p style={{
          margin: `${theme.spacing.md} 0 0 0`,
          fontSize: theme.typography.sizes.sm,
          color: theme.colors.text + '70'
        }}>
          Signé le {new Date(data.date_signature).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      )}
    </FormSection>
  );
};
